/// <reference types="vite/client" />
/**
 * /metrics — Y4 (#164) metrics panels (cost, verdicts, throttling).
 * All three panels react to the global EngagementContext (Y5).
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { ErrorBanner } from "../components/ErrorBanner";
import { useEngagement } from "../hooks/useEngagement";

// ─── Wire-format mirrors ──────────────────────────────────────────────

interface AgentMetricsRow {
  agent: string;
  date: string;
  llmCalls: number;
  llmTokensIn: number;
  llmTokensOut: number;
  verdictsPass: number;
  verdictsFail: number;
  verdictsConditional: number;
  verdictsBlock: number;
  rateLimitDenials: number;
}

interface VerdictRateRow {
  date: string;
  pass: number;
  conditional: number;
  fail: number;
  block: number;
}

interface ThrottlingRow {
  agent: string;
  date: string;
  denials: number;
}

interface RecentLlmCall {
  timestamp: string;
  agent: string;
  task: string;
  model: string;
  tenantId: string;
  outcome: string;
  promptTokens: number;
  completionTokens: number;
  durationMs: number;
  cacheHit: boolean;
}

// ─── Cost model ───────────────────────────────────────────────────────
// Token → cost: simple per-1k token estimate so the panel surfaces a
// meaningful number even without a real billing feed. Tune later when
// we record the actual model/cost on the metric event.
const COST_PER_1K_IN_CENTS = 0.3;
const COST_PER_1K_OUT_CENTS = 1.5;

function tokensToCents(row: AgentMetricsRow): number {
  return (
    (row.llmTokensIn * COST_PER_1K_IN_CENTS) / 1000 +
    (row.llmTokensOut * COST_PER_1K_OUT_CENTS) / 1000
  );
}

function formatCents(cents: number): string {
  if (cents < 1) return `${cents.toFixed(2)}¢`;
  return `€${(cents / 100).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// ─── Date range ───────────────────────────────────────────────────────

const RANGES: Array<{ label: string; days: number }> = [
  { label: "7d", days: 7 },
  { label: "30d", days: 30 },
  { label: "90d", days: 90 },
];

// ─── Page ─────────────────────────────────────────────────────────────

export function Metrics() {
  const { engagementId } = useEngagement();
  const [rangeDays, setRangeDays] = useState(7);
  const [agentRows, setAgentRows] = useState<AgentMetricsRow[]>([]);
  const [verdictRows, setVerdictRows] = useState<VerdictRateRow[]>([]);
  const [throttlingRows, setThrottlingRows] = useState<ThrottlingRow[]>([]);
  const [recentLlm, setRecentLlm] = useState<RecentLlmCall[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    (signal?: AbortSignal) => {
      setLoading(true);
      setError(null);
      const sinceMs = Date.now() - rangeDays * 24 * 60 * 60 * 1000;
      const params = new URLSearchParams({ since: String(sinceMs) });
      if (engagementId) params.set("engagement", engagementId);
      const qs = `?${params.toString()}`;
      Promise.all([
        fetch(`/api/metrics/agent${qs}`, { signal }).then((r) => (r.ok ? r.json() : [])),
        fetch(`/api/metrics/verdicts${qs}`, { signal }).then((r) => (r.ok ? r.json() : [])),
        fetch(`/api/metrics/throttling${qs}`, { signal }).then((r) => (r.ok ? r.json() : [])),
        fetch(`/api/llm/recent?limit=50`, { signal }).then((r) => (r.ok ? r.json() : [])),
      ])
        .then(([agent, verdicts, throttling, llmRecent]) => {
          setAgentRows(Array.isArray(agent) ? agent : []);
          setVerdictRows(Array.isArray(verdicts) ? verdicts : []);
          setThrottlingRows(Array.isArray(throttling) ? throttling : []);
          setRecentLlm(Array.isArray(llmRecent) ? llmRecent : []);
          setLoading(false);
        })
        .catch((err: Error) => {
          if (err.name === "AbortError") return;
          setError(err.message);
          setLoading(false);
        });
    },
    [engagementId, rangeDays],
  );

  useEffect(() => {
    const ctrl = new AbortController();
    load(ctrl.signal);
    return () => ctrl.abort();
  }, [load]);

  // /api/llm/recent is the only live-ish panel — refresh it on a 30s
  // tick so the dashboard tracks AI Gateway spikes without a page reload.
  useEffect(() => {
    const ctrl = new AbortController();
    const tick = () => {
      fetch(`/api/llm/recent?limit=50`, { signal: ctrl.signal })
        .then((r) => (r.ok ? r.json() : []))
        .then((rows) => {
          if (Array.isArray(rows)) setRecentLlm(rows);
        })
        .catch(() => {
          // swallow — the panel falls back to whatever it had.
        });
    };
    const id = setInterval(tick, 30_000);
    return () => {
      clearInterval(id);
      ctrl.abort();
    };
  }, []);

  if (error) {
    return <ErrorBanner message={error} onRetry={load} context="metrics" />;
  }

  return (
    <div className="space-y-8">
      <header className="reveal flex flex-col md:flex-row md:items-end md:justify-between gap-4" style={{ animationDelay: "0ms" }}>
        <div>
          <p className="eyebrow">Console · 06</p>
          <h2 className="font-display italic text-3xl sm:text-4xl lg:text-[56px] leading-[0.95] mt-2 tracking-tight">
            <span className="text-[var(--color-text)]">Swarm </span>
            <span className="text-accent-gradient">Metrics</span>
          </h2>
          <p className="mt-3 text-sm text-[var(--color-text-muted)] max-w-2xl">
            LLM cost, verifier verdict rate, and rate-limit throttling — sliced by engagement.
          </p>
        </div>

        <div className="flex gap-1" role="group" aria-label="Date range">
          {RANGES.map((r) => {
            const active = r.days === rangeDays;
            return (
              <button
                key={r.days}
                onClick={() => setRangeDays(r.days)}
                className={`font-mono text-xs px-3 py-2 rounded transition-colors ${
                  active
                    ? "text-[var(--color-accent-glow)]"
                    : "text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
                }`}
                style={{
                  background: active ? "var(--color-accent-soft)" : "var(--color-bg-card)",
                  border: `1px solid ${active ? "var(--color-accent)" : "var(--color-border)"}`,
                }}
              >
                {r.label}
              </button>
            );
          })}
        </div>
      </header>

      {loading && agentRows.length === 0 && (
        <div className="flex items-center gap-3 text-[var(--color-text-dim)]">
          <span className="live-dot text-[var(--color-accent)]" />
          <span className="font-mono text-xs tracking-[0.18em] uppercase blink">Loading metrics…</span>
        </div>
      )}

      <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <CostPanel rows={agentRows} />
        <VerdictPanel rows={verdictRows} />
        <ThrottlingPanel rows={throttlingRows} />
      </section>

      <section>
        <RecentLlmPanel rows={recentLlm} />
      </section>
    </div>
  );
}

// ─── Panels ───────────────────────────────────────────────────────────

function EmptyPanel({ title, hint }: { title: string; hint: string }) {
  return (
    <div className="panel p-4 sm:p-6 min-h-[240px] sm:min-h-[280px] flex flex-col">
      <p className="eyebrow">{title}</p>
      <div className="flex-1 flex flex-col items-center justify-center text-center gap-2">
        <p className="font-display italic text-2xl text-[var(--color-text-muted)]">No data yet</p>
        <p className="font-mono text-[10px] tracking-wider uppercase text-[var(--color-text-dim)] max-w-xs">{hint}</p>
      </div>
    </div>
  );
}

function CostPanel({ rows }: { rows: AgentMetricsRow[] }) {
  const byAgent = useMemo(() => {
    const map = new Map<string, number>();
    for (const r of rows) map.set(r.agent, (map.get(r.agent) ?? 0) + tokensToCents(r));
    return Array.from(map.entries())
      .map(([agent, cents]) => ({ agent, cents }))
      .sort((a, b) => b.cents - a.cents);
  }, [rows]);

  const total = byAgent.reduce((s, x) => s + x.cents, 0);

  if (byAgent.length === 0) {
    return <EmptyPanel title="LLM Cost · per agent" hint="No LLM activity in this window for this engagement." />;
  }

  const max = Math.max(...byAgent.map((b) => b.cents), 1);

  return (
    <div className="panel p-4 sm:p-6 min-h-[240px] sm:min-h-[280px]">
      <div className="flex items-end justify-between mb-4">
        <p className="eyebrow">LLM Cost · per agent</p>
        <p className="font-display italic text-2xl tabular-nums text-[var(--color-accent-glow)]">{formatCents(total)}</p>
      </div>
      <ul className="space-y-2">
        {byAgent.map((b) => (
          <li key={b.agent} className="space-y-1">
            <div className="flex items-baseline justify-between font-mono text-[11px]">
              <span className="text-[var(--color-text)]">{b.agent}</span>
              <span className="text-[var(--color-text-muted)] tabular-nums">{formatCents(b.cents)}</span>
            </div>
            <div className="h-1.5 rounded bg-[var(--color-bg-card)] overflow-hidden">
              <div
                className="h-full"
                style={{ width: `${(b.cents / max) * 100}%`, background: "var(--color-accent)" }}
              />
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function VerdictPanel({ rows }: { rows: VerdictRateRow[] }) {
  if (rows.length === 0) {
    return <EmptyPanel title="Verifier Verdicts · per day" hint="No verifier activity in this window." />;
  }

  // Show most-recent 14 dates so the row is dense but legible.
  const slice = rows.slice(0, 14).reverse();

  return (
    <div className="panel p-4 sm:p-6 min-h-[240px] sm:min-h-[280px]">
      <p className="eyebrow mb-3">Verifier Verdicts · per day</p>
      <table className="w-full text-[11px] font-mono">
        <thead>
          <tr className="text-[var(--color-text-dim)]">
            <th className="text-left font-normal pb-1">Date</th>
            <th className="text-right font-normal pb-1" style={{ color: "var(--color-status-active)" }}>PASS</th>
            <th className="text-right font-normal pb-1" style={{ color: "var(--color-status-paused)" }}>COND</th>
            <th className="text-right font-normal pb-1" style={{ color: "var(--color-status-error)" }}>FAIL</th>
            <th className="text-right font-normal pb-1" style={{ color: "var(--color-text-muted)" }}>BLOCK</th>
          </tr>
        </thead>
        <tbody>
          {slice.map((r) => (
            <tr key={r.date} className="text-[var(--color-text-muted)]">
              <td className="py-0.5">{r.date}</td>
              <td className="text-right tabular-nums" style={{ color: "var(--color-status-active)" }}>{r.pass}</td>
              <td className="text-right tabular-nums" style={{ color: "var(--color-status-paused)" }}>{r.conditional}</td>
              <td className="text-right tabular-nums" style={{ color: "var(--color-status-error)" }}>{r.fail}</td>
              <td className="text-right tabular-nums">{r.block}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ThrottlingPanel({ rows }: { rows: ThrottlingRow[] }) {
  if (rows.length === 0) {
    return <EmptyPanel title="Rate-limit Denials" hint="No throttling events in this window — healthy budgets." />;
  }
  const total = rows.reduce((s, r) => s + r.denials, 0);
  return (
    <div className="panel p-4 sm:p-6 min-h-[240px] sm:min-h-[280px]">
      <div className="flex items-end justify-between mb-3">
        <p className="eyebrow">Rate-limit Denials</p>
        <p className="font-display italic text-2xl tabular-nums text-[var(--color-status-error)]">{total}</p>
      </div>
      <table className="w-full text-[11px] font-mono">
        <thead>
          <tr className="text-[var(--color-text-dim)]">
            <th className="text-left font-normal pb-1">Date</th>
            <th className="text-left font-normal pb-1">Agent</th>
            <th className="text-right font-normal pb-1">Denials</th>
          </tr>
        </thead>
        <tbody>
          {rows.slice(0, 20).map((r, i) => (
            <tr key={`${r.date}-${r.agent}-${i}`} className="text-[var(--color-text-muted)]">
              <td className="py-0.5">{r.date}</td>
              <td>{r.agent}</td>
              <td className="text-right tabular-nums text-[var(--color-status-error)]">{r.denials}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Recent LLM panel (issue #23) ─────────────────────────────────────
// Streams the last 50 LLM calls from Analytics Engine. Refreshes every
// 30s (driven from the parent component). Filters: agent, task, outcome.

const RECENT_LLM_OUTCOMES: Array<{ key: string; label: string }> = [
  { key: "", label: "all" },
  { key: "ok", label: "ok" },
  { key: "error", label: "error" },
];

function RecentLlmPanel({ rows }: { rows: RecentLlmCall[] }) {
  const [agentFilter, setAgentFilter] = useState<string>("");
  const [taskFilter, setTaskFilter] = useState<string>("");
  const [outcomeFilter, setOutcomeFilter] = useState<string>("");

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (agentFilter && r.agent !== agentFilter) return false;
      if (taskFilter && r.task !== taskFilter) return false;
      if (outcomeFilter && r.outcome !== outcomeFilter) return false;
      return true;
    });
  }, [rows, agentFilter, taskFilter, outcomeFilter]);

  const agents = useMemo(() => Array.from(new Set(rows.map((r) => r.agent))).sort(), [rows]);
  const tasks = useMemo(() => Array.from(new Set(rows.map((r) => r.task))).filter((t) => t && t !== "n/a").sort(), [rows]);

  if (rows.length === 0) {
    return (
      <div className="panel p-4 sm:p-6">
        <p className="eyebrow">LLM calls · live (last 50)</p>
        <div className="mt-6 text-center text-[var(--color-text-muted)]">
          <p className="font-display italic text-2xl">No LLM activity yet.</p>
          <p className="font-mono text-[10px] tracking-wider uppercase text-[var(--color-text-dim)] mt-2">
            Will populate from Analytics Engine on the next agent heartbeat.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="panel p-4 sm:p-6">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-3">
        <div>
          <p className="eyebrow">LLM calls · live (last 50)</p>
          <p className="font-mono text-[10px] text-[var(--color-text-dim)] mt-1">
            Refreshes every 30s · {filtered.length} of {rows.length} shown
          </p>
        </div>
        <div className="flex flex-wrap gap-2 font-mono text-[10px]">
          <select
            value={agentFilter}
            onChange={(e) => setAgentFilter(e.target.value)}
            className="bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded px-2 py-1 text-[var(--color-text)]"
            aria-label="Filter by agent"
          >
            <option value="">all agents</option>
            {agents.map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
          <select
            value={taskFilter}
            onChange={(e) => setTaskFilter(e.target.value)}
            className="bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded px-2 py-1 text-[var(--color-text)]"
            aria-label="Filter by task"
          >
            <option value="">all tasks</option>
            {tasks.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
          <select
            value={outcomeFilter}
            onChange={(e) => setOutcomeFilter(e.target.value)}
            className="bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded px-2 py-1 text-[var(--color-text)]"
            aria-label="Filter by outcome"
          >
            {RECENT_LLM_OUTCOMES.map((o) => (
              <option key={o.key} value={o.key}>{o.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-[11px] font-mono">
          <thead>
            <tr className="text-[var(--color-text-dim)]">
              <th className="text-left font-normal pb-1">Time</th>
              <th className="text-left font-normal pb-1">Agent</th>
              <th className="text-left font-normal pb-1">Task</th>
              <th className="text-left font-normal pb-1">Model</th>
              <th className="text-right font-normal pb-1">In</th>
              <th className="text-right font-normal pb-1">Out</th>
              <th className="text-right font-normal pb-1">Dur</th>
              <th className="text-left font-normal pb-1">Cache</th>
              <th className="text-left font-normal pb-1">Outcome</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r, i) => (
              <tr key={`${r.timestamp}-${i}`} className="text-[var(--color-text-muted)]">
                <td className="py-0.5 whitespace-nowrap">{r.timestamp.slice(11, 19)}</td>
                <td className="text-[var(--color-text)]">{r.agent}</td>
                <td>{r.task || "—"}</td>
                <td className="text-[var(--color-text-dim)]">{r.model}</td>
                <td className="text-right tabular-nums">{r.promptTokens}</td>
                <td className="text-right tabular-nums">{r.completionTokens}</td>
                <td className="text-right tabular-nums">{r.durationMs}ms</td>
                <td>{r.cacheHit ? "HIT" : ""}</td>
                <td
                  className={r.outcome === "ok"
                    ? "text-[var(--color-status-active)]"
                    : "text-[var(--color-status-error)]"}
                >
                  {r.outcome}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
